import type { JSX } from 'react';

import { useTheme } from '../hooks';

/**
 * Sun/moon segmented toggle. Reads from `useTheme`, writes with `setTheme`.
 *
 * The visual (animated pill that slides to the sun or moon side) is entirely
 * provided by `.shell__theme-toggle` in `tokens.css`. This component renders
 * the two icons and flips `body.dataset.mode`, which the CSS selectors
 * `[data-mode="light"] .tt-sun` and `[data-mode="dark"] .tt-moon` read to
 * fade the opposite icon out.
 */
export function ThemeToggle(): JSX.Element {
  const { isDark, setTheme } = useTheme();
  const next = isDark ? 'light' : 'dark';
  const title = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <button
      type="button"
      className="shell__theme-toggle"
      onClick={() => setTheme(next)}
      aria-label={title}
      title={title}
    >
      <svg
        className="tt-sun"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
      <svg
        className="tt-moon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    </button>
  );
}
